import * as React from 'react';
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsClipPath } from '@mui/x-charts/ChartsClipPath';
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight';
import { LineHighlightPlot, LinePlot } from '@mui/x-charts/LineChart';
import {
  dateTickFormat,
  dateFormat,
  elevationFormatter,
} from '../utils/format';
import { useHighlightedAxis } from './HighlightedAxisContext';
import NightOverlay from './NightOverlay';
import useId from '@mui/utils/useId';
import WindOverlay from './WindOverlay';

function getLabel(label, elevation, elevationLabel) {
  if (elevationLabel === true || elevationLabel === undefined) {
    return `${elevationFormatter.format(elevation)} ft ${label}`;
  }
  return label;
}

export default function WeatherChart({
  forecast,
  left,
  right,
  secondaryRight,
  underlay,
  overlay,
  xAxisPosition = 'bottom',
  height = 130,
  showWind = false,
}) {
  const { highlightedAxis, setHighlightedAxis } = useHighlightedAxis();
  const { elevation, times, data } = forecast;
  const id = useId();
  const clipPathId = `${id}-clip-path`;

  const axes = [
    {
      id: 'left',
      width: 80,
      valueFormatter: left.valueFormatter,
    },
    {
      id: 'right',
      width: 60,
      position: 'right',
      valueFormatter: right ? right.valueFormatter : null,
    },
  ];

  const secondaryRightAxis = {
    id: 'right2',
    width: 80,
    position: 'right',
    valueFormatter: secondaryRight ? secondaryRight.valueFormatter : null,
  };

  const leftSeries = {
    type: 'line',
    label: getLabel(left.label, elevation, left.elevationLabel),
    data: data[left.quantity],
    valueFormatter: left.valueFormatter,
    showMark: false,
    color: left.color,
    yAxisId: 'left',
  };

  const rightSeries = right
    ? {
        type: 'line',
        label: getLabel(right.label, elevation, right.elevationLabel),
        data: data[right.quantity],
        valueFormatter: right.valueFormatter,
        showMark: false,
        color: right.color,
        yAxisId: 'right',
      }
    : null;

  const secondaryRightSeries = secondaryRight
    ? {
        type: 'line',
        label: getLabel(
          secondaryRight.label,
          elevation,
          secondaryRight.elevationLabel
        ),
        data: data[secondaryRight.quantity],
        valueFormatter: secondaryRight.valueFormatter,
        showMark: false,
        color: secondaryRight.color,
        yAxisId: 'right2',
      }
    : null;

  return (
    <ChartContainer
      xAxis={[
        {
          data: times,
          id: 'x-axis',
          domainLimit: 'strict',
          scaleType: 'time',
          valueFormatter: (date, context) =>
            context.location === 'tick'
              ? dateTickFormat(date)
              : dateFormat(date),
          position: xAxisPosition,
        },
      ]}
      yAxis={[...axes, ...(secondaryRight ? [secondaryRightAxis] : [])]}
      series={[
        leftSeries,
        ...(right ? [rightSeries] : []),
        ...(secondaryRight ? [secondaryRightSeries] : []),
      ]}
      height={xAxisPosition !== 'none' ? height : height - 20}
      highlightedAxis={highlightedAxis}
      onHighlightedAxisChange={setHighlightedAxis}
      margin={{
        bottom: 10,
        top: showWind ? 48 : 15,
      }}
    >
      <g clipPath={`url(#${clipPathId})`}>
        <NightOverlay times={times} />
        <ChartsGrid vertical horizontal />
        {underlay}
        <LinePlot />
        {overlay}
      </g>
      {showWind && <WindOverlay forecast={forecast} label="speed" y={20} />}
      <ChartsClipPath id={clipPathId} />
      <LineHighlightPlot />
      <ChartsAxisHighlight x="line" />
      <ChartsXAxis />
      <ChartsYAxis axisId="left" label={left.label} />
      <ChartsYAxis axisId="right" label={right ? right.label : null} />
      {secondaryRight && (
        <ChartsYAxis axisId="right2" label={secondaryRight.label} />
      )}
      <ChartsTooltip />
    </ChartContainer>
  );
}
